import React, { useState, useRef, useEffect } from 'react';
import QueueService from './QueueService';
import './WhatsappChatModal.css';
import MarkdownParser from './MarkdownParser';

const WhatsAppChatModal = ({ messages, phone_number, onClose, token, onMessageSent, phone_number_id, country_code }) => {
  const [newMessage, setNewMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        if (!phone_number || !phone_number_id) {
          console.error('Missing required props: phone_number or phone_number_id');
          return;
        }
        // Use country_code if available, otherwise default to "91"
        const code = country_code || "91";
        const formattedPhoneNumber = `${code}${phone_number}`;
        const response = await QueueService.FetchMessages(formattedPhoneNumber, phone_number_id, 10);
        if (response.messages) {
          // Transform the messages to match the expected format
          const transformedMessages = response.messages.map(msg => ({
            message_text: msg.message_metadata?.template?.components?.[0]?.parameters
              ?.map(param => param.text)
              .join(' ') || msg.message_text || '',
            sent_by_system: msg.sender_id === phone_number_id,
            timestamp: msg.timestamp
          }));
          setChatMessages(transformedMessages);
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [phone_number, phone_number_id, country_code]);

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;

    try {
      // Use country_code if available, otherwise default to "91"
      const code = country_code || "91";
      const formattedPhoneNumber = `${code}${phone_number}`;
      const response = await QueueService.SendWhatsappMessage(formattedPhoneNumber, newMessage, token, phone_number_id, 20);
      if (response.status?.toLowerCase() === "success") {
        const newMsg = {
          message_text: newMessage,
          sent_by_system: true,
          timestamp: new Date(new Date().getTime() - (5 * 60 + 30) * 60 * 1000).toISOString()
        };
        setNewMessage('');
        setChatMessages(prev => [...prev, newMsg]);
        if (onMessageSent) {
          onMessageSent(newMsg);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <div className="whatsapp-chat-modal">
        <div className="chat-header">
          <h3>WhatsApp Chat</h3>
          <button className="whatsapp-close-button" onClick={onClose}>&times;</button>
        </div>
        
        <div className="chat-messages">
          {loading ? (
            <div className="loading">Loading messages...</div>
          ) : (
            chatMessages.map((msg, index) => (
              <div
                key={index}
                className={`message ${msg.sent_by_system ? 'system' : 'customer'}`}
              >
                <div className="message-content">
                  <MarkdownParser text={msg.message_text.length > 150 ? `${msg.message_text.substring(0, 150)}...` : msg.message_text} />
                  <span className="timestamp">
                    {new Date(new Date(msg.timestamp).getTime() + (5 * 60 + 30) * 60000).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </span>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
          />
          <button onClick={handleSend} disabled={!newMessage.trim()}>
            Send
          </button>
        </div>
      </div>
    </>
  );
};

export default WhatsAppChatModal;